import { useEffect, useState } from "react";

/**
 * PastoralProgress
 *
 * Animated progress affordance shown while a long-running pastoral
 * task is in flight. Used on the Discernment and Ladder Progress
 * pages so users see continuous movement instead of a static
 * "Discerning…" label that makes the app feel hung.
 *
 * Two driving modes:
 *
 *   1. SERVER-DRIVEN — pass `phase` (a string updated by polling the
 *      backend job). When the phase changes, the bar visibly jumps
 *      forward to reflect new progress. Use this for engines that
 *      have a real job/polling pattern (Progress).
 *
 *   2. CLIENT-ONLY — leave `phase` undefined and pass
 *      `clientPhases` (an array of pastoral labels) + `estSeconds`
 *      (the expected duration). The bar fills smoothly to ~95% over
 *      that window and the label rotates through `clientPhases` so
 *      the user never sees a static screen. Use this for engines
 *      that run a synchronous POST without progress emission
 *      (Discernment).
 *
 * When `done` flips true (e.g. polling sees status==='ready' or the
 * synchronous POST resolves), the bar jumps to 100% and the active
 * label is replaced with `doneLabel`.
 */
export default function PastoralProgress({
  phase,
  clientPhases,
  estSeconds = 45,
  done = false,
  doneLabel = "Done.",
  testIdPrefix = "pastoral-progress",
}) {
  // Smooth-fill progress in client-only mode.
  const [pct, setPct] = useState(0);
  // Rotating client-phase index (client-only mode).
  const [clientIdx, setClientIdx] = useState(0);

  useEffect(() => {
    if (phase !== undefined || done) return;
    // Client-only smooth fill: from 0 → 95% over estSeconds. Capped
    // at 95 so the bar visibly snaps to 100% only when `done` flips.
    const TICK_MS = 250;
    const target = 95;
    const ticks = Math.max(1, Math.floor((estSeconds * 1000) / TICK_MS));
    const step = target / ticks;
    let t = 0;
    const id = setInterval(() => {
      t += 1;
      setPct((p) => {
        if (p >= target) return p;
        return Math.min(target, p + step);
      });
      if (t > ticks * 3) clearInterval(id);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [phase, done, estSeconds]);

  useEffect(() => {
    if (phase !== undefined || done) return;
    if (!clientPhases || clientPhases.length === 0) return;
    const id = setInterval(() => {
      setClientIdx((i) => (i + 1) % clientPhases.length);
    }, 7000);
    return () => clearInterval(id);
  }, [phase, done, clientPhases]);

  // Server-driven mode: when phase changes, bump the bar forward.
  const [serverPct, setServerPct] = useState(8);
  const [seenPhases, setSeenPhases] = useState(new Set());
  useEffect(() => {
    if (phase === undefined || done) return;
    setSeenPhases((prev) => {
      if (prev.has(phase)) return prev;
      const next = new Set(prev);
      next.add(phase);
      // Each new phase advances the bar ~9% — but cap at 92% so the
      // bar snaps to 100 only when done.
      setServerPct((p) => Math.min(92, p + 9));
      return next;
    });
  }, [phase, done]);

  // Continuous-drift while server phase is stable so the bar never
  // sits completely still between backend phase changes.
  useEffect(() => {
    if (phase === undefined || done) return;
    const id = setInterval(() => {
      setServerPct((p) => (p < 92 ? Math.min(92, p + 0.3) : p));
    }, 1500);
    return () => clearInterval(id);
  }, [phase, done]);

  const isServer = phase !== undefined;
  const displayedPct = done ? 100 : isServer ? serverPct : pct;
  const displayedLabel = done
    ? doneLabel
    : isServer
    ? (phase || "Working…")
    : (clientPhases && clientPhases[clientIdx]) || "Working…";

  return (
    <div
      className="w-full max-w-md mx-auto"
      data-testid={`${testIdPrefix}-root`}
      aria-live="polite"
    >
      {/* Rotating pastoral label */}
      <div
        className="flex items-center justify-center gap-3 mb-3 text-[#FFE7A0] font-cinzel text-[0.7rem] sm:text-xs tracking-[0.28em] uppercase text-center"
        data-testid={`${testIdPrefix}-label`}
      >
        <span
          className="inline-block h-2.5 w-2.5 rounded-full bg-[#FFE7A0] animate-pulse"
          aria-hidden="true"
        />
        <span>{displayedLabel}</span>
      </div>
      {/* Animated bar */}
      <div
        className="h-1.5 w-full rounded-full bg-[#FFE7A0]/15 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(displayedPct)}
        data-testid={`${testIdPrefix}-bar`}
      >
        <div
          className="h-full bg-gradient-to-r from-[#FFE7A0] via-[#F0E6D2] to-[#FFE7A0] rounded-full transition-all duration-700 ease-out"
          style={{ width: `${displayedPct}%` }}
        />
      </div>
      {/* Pct readout */}
      <div className="mt-2 text-center text-[0.65rem] tracking-[0.2em] uppercase text-[#F0E6D2]/55 font-cinzel" data-testid={`${testIdPrefix}-pct`}>
        {Math.round(displayedPct)}%
      </div>
    </div>
  );
}
